import React, { FC, useState, useEffect, useMemo } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import { getJourneyUiThemeHack } from '../helpers/themes/get-theme-properties';
import { NeueCheckmarkStrongIcon } from 'src/monet/icons';

const TILE_WIDTH = 107.5;
const TILE_HEIGHT = 80;

function getRoundedCornerClass(i: number): string {
  switch(i) {
    case 0: return 'rounded-tl';
    case 1: return 'rounded-tr';
    case 2: return 'rounded-bl';
    case 3: return 'rounded-br'; 
  }
  return '';
}

type ImageTile = {
  show: boolean;
  url?: string;
};

type DalleImageTilesProps = {
  selectedDalleUrl: Nullable<string>;
  imageTiles: ImageTile[];
  onSelectDalleImage: (imageUrl: string, imageUrls: string[]) => void;
  dalleLoading: boolean;
  dalleImageListeners?: {
    onLoad: (url: string) => void;
  };
};

export const DalleImageTiles = ({
  selectedDalleUrl,
  imageTiles,
  onSelectDalleImage,
  dalleLoading,
  dalleImageListeners,
}: DalleImageTilesProps) => {
  const loadingAnimationApi = useSpringRef();
  const [loadingAnimationProps] = useSpring(
    () => ({
      ref: loadingAnimationApi,
      from: { backgroundPositionX: '150%' },
      config: { duration: 800 },
    }),
    []
  );

  useEffect(() => {
    if (dalleLoading) {
      loadingAnimationApi.start({
        from: { backgroundPositionX: '100%' },
        to: [{ backgroundPositionX: '0%' }, { backgroundPositionX: '100%' }],
        config: { duration: 4000 },
        loop: true,
      });
    } else {
      loadingAnimationApi.stop();
    }
  }, [dalleLoading]);

  const journeyUiTheme = React.useMemo(() => getJourneyUiThemeHack(), []);
  if (imageTiles.length <= 1) {
    return null;
  }

  return (
    <div className='flex flex-wrap gap-[1px]'>
      {imageTiles.map((image, i) => {
        const roundedCornerClass = getRoundedCornerClass(i);
        return (
          <div
            key={image.url ? `${image.url}` : `${i}`}
            className={classNames('relative flex items-center justify-center transition', roundedCornerClass)}
            style={{
              width: `${TILE_WIDTH}px`,
              height: `${TILE_HEIGHT}px`,
            }}
            onClick={() => {
              if (image.url && selectedDalleUrl !== image.url) {
                onSelectDalleImage(
                  image.url,
                  imageTiles.map((image) => image.url || '')
                );
              }
            }}
          >
            <div
              className={classNames('absolute inset-0', {
                'bg-neue-journey-bg-90': journeyUiTheme === 'dark',
                'bg-[#EEEEEE]': journeyUiTheme === 'light',
                'opacity-100': dalleLoading,
                'opacity-0': !dalleLoading,
              })}
            >
              {dalleLoading && (
                <animated.div
                  className={classNames('absolute inset-0 transition bg-center bg-cover', roundedCornerClass)}
                  style={{
                    backgroundPositionY: '50%',
                    backgroundSize: '400% 400%',
                    backgroundPositionX: loadingAnimationProps.backgroundPositionX,
                    // @ts-ignore
                    '--dalle-tile-loader-bg-1':
                      journeyUiTheme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(16, 16, 16, 0.2)',
                    // @ts-ignore
                    '--dalle-tile-loader-bg-2':
                      journeyUiTheme === 'dark' ? 'rgba(255, 255, 255, 0)' : 'rgba(16, 16, 16, 0)',
                    backgroundImage: `linear-gradient(135deg, var(--dalle-tile-loader-bg-1) 0%, var(--dalle-tile-loader-bg-2) 56.4%, var(--dalle-tile-loader-bg-1) 100%)`,
                  }}
                ></animated.div>
              )}
            </div>
            {image.url && (
              <img
                draggable={false}
                alt={`Dallé ${i}`}
                src={image.url}
                className={classNames('absolute inset-0 w-full h-full object-cover transition', roundedCornerClass, {
                  'opacity-0': dalleLoading,
                  'opacity-100': !dalleLoading,
                })}
                onLoad={() => image.url && dalleImageListeners && dalleImageListeners.onLoad(image.url)}
              />
            )}
            <div
              className={classNames('absolute inset-0 transition cursor-pointer opacity-30', roundedCornerClass, {
                'bg-transparent hover:bg-neue-journey-bg': image.url,
                'bg-neue-journey-bg': selectedDalleUrl && selectedDalleUrl === image.url,
                'opacity-0': dalleLoading,
                'opacity-100': !dalleLoading,
              })}
            ></div>
            {
              selectedDalleUrl === image.url && (
                <div className={classNames('absolute transition', {
                  'opacity-0': dalleLoading,
                  'opacity-100': !dalleLoading,
                })}>
                  <NeueCheckmarkStrongIcon />
                </div>
              )
            }
          </div>
        );
      })}
    </div>
  );
};
